import { useState } from 'react';
import { Download, Loader2, CheckCircle, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const guides = {
  'brisbane-zoning-cheat-sheet': {
    title: 'Brisbane Zoning Cheat Sheet 2026',
    description: 'Every residential zoning code explained in plain English. What you can build, height limits, and value implications — all 12 SEQ councils.',
    pages: '24 pages',
  },
  'selling-to-developer-guide': {
    title: 'The Complete Guide to Selling Your Property to a Developer',
    description: 'Step-by-step: how the process works, what to expect, how to maximise your price, and the mistakes that cost sellers thousands.',
    pages: '18 pages',
  },
  'property-overlay-guide-seq': {
    title: 'SEQ Property Overlay Guide',
    description: 'Flood, character, heritage, vegetation, bushfire — what each overlay means for your property and your options.',
    pages: '16 pages',
  },
};

const LeadMagnetCTA = ({ guideSlug = 'brisbane-zoning-cheat-sheet', sourceArticle = '' }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const guide = guides[guideSlug] || guides['brisbane-zoning-cheat-sheet'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/forms/lead-magnet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, guide_slug: guideSlug, source_article: sourceArticle }),
      });
      if (res.ok) setSubmitted(true);
      else setError('Something went wrong. Please try again.');
    } catch { setError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="my-10 bg-[#042E0C] border border-[#C4A36B]/30 p-6 sm:p-8 text-center" data-testid="lead-magnet-success">
        <CheckCircle size={36} className="mx-auto text-[#C4A36B] mb-3" />
        <h4 className="font-heading text-lg text-white mb-1">Check Your Inbox</h4>
        <p className="text-white/50 text-sm">We've sent the {guide.title} to your email. It should arrive within a few minutes.</p>
      </div>
    );
  }

  return (
    <div className="my-10 bg-[#FFF4EA] border border-[#C4A36B]/30 overflow-hidden" data-testid="lead-magnet-cta">
      <div className="flex flex-col sm:flex-row">
        {/* Left: Guide info */}
        <div className="flex-1 p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-[#C4A36B]" />
            <span className="text-xs font-semibold tracking-[0.15em] uppercase text-[#C4A36B]">Free Download</span>
          </div>
          <h4 className="font-heading text-lg sm:text-xl text-[#042E0C] mb-2 leading-snug">{guide.title}</h4>
          <p className="text-[#042E0C]/60 text-sm mb-4 leading-relaxed">{guide.description}</p>
          <span className="text-xs text-[#042E0C]/40">{guide.pages} &middot; PDF</span>
        </div>

        {/* Right: Form */}
        <div className="sm:w-72 bg-[#042E0C] p-6 sm:p-8 flex flex-col justify-center">
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              data-testid="lead-magnet-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              className="w-full px-3 py-2.5 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
            />
            <input
              data-testid="lead-magnet-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email *"
              className="w-full px-3 py-2.5 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
            />
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <button
              type="submit"
              data-testid="lead-magnet-submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#C4A36B] text-[#042E0C] rounded-lg font-medium text-sm hover:bg-[#C4A36B]/90 transition-colors disabled:opacity-50"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <><Download size={14} /> Get Free Guide</>}
            </button>
          </form>
          <p className="text-white/20 text-xs mt-2 text-center">No spam. Unsubscribe anytime.</p>
        </div>
      </div>
    </div>
  );
};

export default LeadMagnetCTA;
